<template>
  <a-layout-sider
    v-if="menus.length || !isRuntime || isMobileOrNarrowScreen"
    v-model="collapsed"
    :class="[
      `xy-sidebar xy-default-sidebar ${sidebarTheme}`,
      {
        'collapsed': collapsed,
        'runtime': isRuntime,
        'miniProgram': isMobileOrNarrowScreen,
      }
    ]"
    :width="computedWidth"
    :theme="sidebarTheme"
    :collapsible="isRuntime"
    :trigger="null"
    collapsed-width="0"
  >
    <!-- Sidebar Header头部菜单 -->
    <template v-if="isMobileOrNarrowScreen">
      <MiniprogramHeader
        :mode="mode"
        :class="{
          'header-hidden': collapsed
        }"
      />
      <div
        class="drawer-mask"
        :class="{'drawer-mask-opened' : !collapsed}"
        :style="maskLeft"
        @click="innerSetCollapsed(true)"
      />
    </template>

    <!-- 应用标题 -->
    <template v-else>
      <template v-if="withTitle">
        <div
          id="xy-sidebar-title"
          :class="{ collapsed: collapsed }"
        >
          <img
            v-if="project.logo"
            :src="project.logo"
            class="xy-sidebar-logo"
          >
          <span
            v-show="!collapsed"
            class="xy-sidebar-text"
          >
            {{ project.name }}
          </span>
        </div>
      </template>

      <!-- 菜单标题 -->
      <template v-if="withMenuTitle">
        <div
          id="xy-sidebar-menu-title"
          :class="{collapsed:collapsed}"
        >
          <div
            v-if="sidebar.groupLogo"
            class="xy-sidebar-logo"
          >
            <public-w-icon-show-new
              :value="sidebar.groupLogo"
              class="logo-icon"
            />
          </div>
          <span
            v-show="!collapsed"
            class="xy-sidebar-text"
          >
            {{ sidebar.groupName || '' }}
          </span>
        </div>
      </template>

      <!-- 搜索框 -->
      <template v-if="withSearch">
        <div id="xy-sidebar-menu-search">
          <a-input
            v-model="searchInput"
            :placeholder="$t('k_d1f52d91')"
            allow-clear
            @change="handleSearch"
          >
            <a-icon
              slot="prefix"
              class="!opacity-80"
              type="search"
            />
          </a-input>
        </div>
      </template>
    </template>

    <XySidebarMenu
      :class="{
        'no-menu-title': !withMenuTitle,
        'hidden': collapsed
      }"
      :menus="searchInput ? searchMenus : menus"
      :selected-keys="selectedKeys"
      :open-keys="searchInput ? searchOpenKeys : openKeys"
      :show-icons="showIcons"
      @openChange="handleMenuOpenChange"
      @click="handleMenuClick"
    />

    <template v-if="!isMobileOrNarrowScreen">
      <!-- Sidebar 宽度调节 -->
      <div
        class="xy-sidebar-resizer"
        @mousedown="startResize"
        @touchstart="startResize"
      />
      <!-- Sidebar 开关 -->
      <CollapseTrigger
        v-if="isRuntime"
        :collapsed="collapsed"
        :is-show-collapse-trigger="isShowCollapseTrigger"
        @toggle-collapsed="innerToggleCollapsed"
      />
    </template>

    <!-- Sidebar 菜单底部无极标识 -->
    <template v-if="isIntranetWork">
      <div
        :class="[`sidebar-identify ${sidebarTheme}`]"
        @click="handleToWujiHome"
      >
        {{ SIDEBAR_IDENTITY }}
      </div>
    </template>
  </a-layout-sider>
</template>

<script>
import { watch, toRefs, ref, computed, getCurrentInstance } from '@vue/composition-api';
import { difference, union, throttle } from 'lodash';

import MiniprogramHeader from '@miniprogram/header/index';
import CollapseTrigger from '@components/sidebar/CollapseTrigger';
import useSidebar from '@composables/useSidebar';
import useLayout from '@composables/useLayout';
import { toPageByMenu } from '@utils/path';
import { useRenderIdentity, SIDEBAR_IDENTITY } from '@/components/sidebar/tdesign/hooks/useRenderIdentity';
import { useSyncToStore } from '@/components/sidebar/tdesign/hooks/useSyncToStore';
import { runtimeSidebarActionReport } from './tool';

import {
  getMatchedMenuKeys,
  getMenuUrl,
  getMatchedGroupId,
  getAllOpenKeys,
  getConfigOpenKeys,
  getGroupNameMap,
  filterSearchMenus,
  getGroupId,
  onActiveMenuChange,
  getDefaultGroup,
} from './utils';
import XySidebarMenu from './menu';
import { useCommonOpenApi } from '@/components/sidebar/tdesign/hooks/useCommonOpenApi';
import { sidebarOpenApiBatchWrap } from '@/components/sidebar/tdesign/util';
import { useAutoUpdateGroupId } from './tdesign/hooks/useUpdateGroupId';
import { useBaseInfo } from './tdesign/hooks/useBaseInfo';
export default {
  name: 'XySidebar',
  components: { MiniprogramHeader, CollapseTrigger, XySidebarMenu },
  inject: {
    wLayout: {
      from: 'wLayout',
      default: null,
    },
    w: {
      from: 'w',
      default: {},
    },
  },
  props: {
    // 模式
    mode: {
      type: String,
      default: 'runtime',
      validator(value) {
        return ['runtime', 'preview'].includes(value);
      },
    },
    previewGroupId: {
      type: String,
      default: '0',
    },
    isMobileOrNarrowScreen: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const instance = getCurrentInstance();
    const { previewGroupId, isMobileOrNarrowScreen, mode } = toRefs(props);
    const openKeys = ref([]); // 当前打开的 openKeys
    const selectedKeys = ref([]); // 当前展开的 SubMenu 菜单项 key 数组
    const sidebar = ref(null); // 当前选中的菜单项 key 数组

    const width = computed(() => sidebar?.value?.width ?? 209);

    const {
      project,
      roles,
      biz,
      isAdmin,
      sidebarGroups,
      visibleSidebarGroups,
      sidebarTheme,
      customSidebar,
    } = useLayout();

    const {
      collapsed,
      toggleCollapsed,
      setCollapsed,
      computedWidth,
      isShowCollapseTrigger,
      maskLeft,
      extraWidth,
      startResize,
      setMobileCollapsed,
    } = useSidebar({ isMobileOrNarrowScreen, width, mode });

    const { options, $route } = useBaseInfo();

    useAutoUpdateGroupId();

    // 初始化预览时的 sidebar 参数
    const initPreviewSidebar = () => {
      sidebar.value = visibleSidebarGroups.value.find(group => group.groupId === previewGroupId.value);
      if (sidebar.value) {
        openKeys.value = getAllOpenKeys(sidebar.value.menus ?? []);
      }
    };

    // 菜单项搜索
    const searchInput = ref('');
    const searchMenus = ref([]);
    const searchOpenKeys = ref([]);
    const handleSearch = throttle(async () => {
      const menus = filterSearchMenus(instance?.proxy?.menus, searchInput.value || '');
      searchMenus.value = menus;
      searchOpenKeys.value = getAllOpenKeys(menus);
    }, 200);

    // 切换菜单组时清空搜索框内容
    watch(() => sidebar.value?.groupId, () => {
      searchInput.value = '';
    });

    const isIntranetWork =  useRenderIdentity();

    useSyncToStore(mode.value);

    const openApi = useCommonOpenApi({
      onMenuChange: (menu) => {
        instance.proxy.handleMenuClick(menu);
      },
      onKeysChange: (updater) => {
        const cur = updater({ expandKeys: openKeys.value, selectedKeys: selectedKeys.value });
        openKeys.value = cur.expandKeys,
        selectedKeys.value = cur.selectedKeys;
      },
    });

    if (props.isMobileOrNarrowScreen) {
      runtimeSidebarActionReport('antd-mobile');
    }

    return {
      openKeys,
      selectedKeys,
      sidebar,
      collapsed,
      toggleCollapsed,
      innerToggleCollapsed: toggleCollapsed,
      setCollapsed,
      ...(sidebarOpenApiBatchWrap({ toggleCollapsed, setCollapsed })),
      innerSetCollapsed: setCollapsed,
      computedWidth,
      isShowCollapseTrigger,
      maskLeft,
      extraWidth, // 暴露extraWidth用来进行单元测试
      startResize,
      setMobileCollapsed,
      project,
      roles,
      biz,
      isAdmin,
      sidebarGroups,
      visibleSidebarGroups,
      sidebarTheme,
      customSidebar,
      initPreviewSidebar,

      // 菜单搜索相关
      searchInput,
      handleSearch,
      searchMenus,
      searchOpenKeys,
      isIntranetWork,
      SIDEBAR_IDENTITY,
      ...openApi,
      route: $route,
      options,
    };
  },
  computed: {
    currentGroupId() {
      return this.sidebar.groupId;
    },
    currentGroupName() {
      return this.sidebar.groupName;
    },
    menus() {
      return this.sidebar?.menus ?? [];
    },
    groupNameMap() {
      return getGroupNameMap(this.visibleSidebarGroups);
    },
    withTitle() {
      return this.sidebar?.withTitle ?? false;
    },
    withMenuTitle() {
      return this.sidebar?.withMenuTitle ?? false;
    },
    // 是否手风琴展开模式
    accordion() {
      return this.sidebar?.accordion ?? false;
    },
    // 是否显示搜索框
    withSearch() {
      return this.sidebar?.withSearch ?? false;
    },
    showIcons() {
      return this?.sidebar?.showIcons ?? [true, false];
    },
    isRuntime() {
      return this.mode === 'runtime';
    },
    stringInteropContext() {
      return {
        ...(this.w?.mainRenderer ?? {}),
        $app: this.$app,
        w: this.w,
      };
    },
  },
  created() {
    if (this.mode === 'preview') {
      this.$watch(() => ({
        project: this.project,
        roles: this.roles,
        biz: this.biz,
        isAdmin: this.isAdmin,
        previewGroupId: this.previewGroupId,
      }), () => {
        this.initPreviewSidebar();
      }, { immediate: true, deep: true });
    } else {
      this.initRuntimeSidebar();
      this.$watch(() => ({
        $route: this.route,
        roles: this.roles,
        biz: this.biz,
        isAdmin: this.isAdmin,
        groupId: getGroupId(),
      }), () => {
        this.computeSidebar();
      }, { deep: true });

      this.$watch(getGroupId, (groupId) => {
        if (!groupId) {
          return;
        }
        this.initOpenKeys(this.getSidebarByGroupId(groupId), []);
      });

      // 自定义菜单改变时, 重新计算一次菜单项
      this.$watch(() => this.customSidebar, () => {
        this.computeSidebar();
      });
    }
  },
  methods: {
    initOpenKeys(sidebar, openKeys) {
      // 计算默认的打开菜单
      if (sidebar.openAll) {
        this.openKeys = union(getAllOpenKeys(sidebar.menus ?? []), openKeys);
      } else if (!sidebar.accordion) {
        this.openKeys = union(getConfigOpenKeys(sidebar.menus ?? []), openKeys);
      }
    },
    // 初始化运行时 sidebar 参数
    initRuntimeSidebar() {
      this.computeSidebar();
      this.initOpenKeys(this.sidebar, this.openKeys);
    },
    getSidebarByGroupId(groupId) {
      return this.visibleSidebarGroups.find(group => `${group.groupId}` === `${groupId}`) || getDefaultGroup() || {};
    },
    // 计算Sidebar的打开状态
    computeSidebar() {
      const groupId = getMatchedGroupId(this.visibleSidebarGroups, this.options);
      this.sidebar = this.getSidebarByGroupId(groupId);

      const menus = this.sidebar?.menus ?? [];
      const {
        openKeys,
        selectedKeys,
        activeMenu,
        parentMenus,
      } = getMatchedMenuKeys(menus, {
        $route: this.route,
        stringInteropContext: this.stringInteropContext,
      });

      // 合并保留当前打开的一级菜单
      if (this.accordion) {
        this.openKeys = union(openKeys, this.openKeys.filter(key => key.includes('.')));
      } else {
        this.openKeys = union(openKeys, this.openKeys);
      }
      this.selectedKeys = selectedKeys;
      onActiveMenuChange({ activeMenu, parentMenus, path: this.route.fullPath });
    },
    // 获取菜单URL
    getMenuUrl,
    // 菜单点击事件: 页面跳转
    handleMenuClick(menu, key) {
      // 重复点击不跳转页面, 但隐藏移动端菜单
      if (key && this.selectedKeys.some(item => item === key)) {
        this.setMobileCollapsed(true);
        if (getMenuUrl(menu) === this.route.path) return;
      };
      toPageByMenu(menu, {
        $router: this.$router,
        $route: this.route,
        stringInteropContext: this.stringInteropContext,
      });
    },
    // 菜单打开事件: 只展开当前父级菜单
    handleMenuOpenChange(openKeys) {
      let nextOpenKey = openKeys;
      let prevKeys = [];
      if (this.searchInput) {
        prevKeys = this.searchOpenKeys;
      } else {
        prevKeys = this.openKeys;
      }
      if (this.accordion) {
        const newFirstOpenKey = openKeys.filter(key => !key.includes('.'));
        const oldFirstOpenKey = prevKeys.filter(key => !key.includes('.'));
        const diffFistOpenKey = difference(newFirstOpenKey, oldFirstOpenKey);
        if (diffFistOpenKey.length > 0) {
          nextOpenKey = openKeys.filter(key => key.includes('.')).concat(diffFistOpenKey);
        }
      }
      if (this.searchInput) {
        this.searchOpenKeys = nextOpenKey;
      } else {
        this.openKeys = nextOpenKey;
      }
    },
    // Sidebar 实例供外部调用的方法
    handleToWujiHome() {
      window.open('https://xy.woa.com/xy/app/prod/portal/index');
    },
  },
};

</script>
<style lang="scss">
$menuItemHeight: var(--xy-custom-sidebar-menu-item-height);

.xy-sidebar {
  &.runtime {
    user-select: none;
    box-shadow: 1px 0 0 0 #eee;
  }

  &.miniProgram {
    border-right: 0;
  }

  // from ui-core/src/comps/Aside.vue
  .xy-sidebar-resizer{
    position: absolute;
    top: 0;
    width: 4px;
    height: 100%;
    margin: 0 -2px;
    cursor: ew-resize;
    z-index: 1;
    right:0;
    &:hover {
      width: 6px;
      margin: 0 -3px;
      background: rgba(#fff, 0.06);
    }
  }

  .drawer-mask{
    position: fixed;
    top: var(--xy-header-height);
    width: 0;
    height: 0;
    transition: background 0.3s ,left 0.3s cubic-bezier(0.78, 0.14, 0.15, 0.86) 0s, width 0s 0.3s , height 0s 0.3s;
    z-index: 1;
    &-opened{
      background: rgba(0,0,0,0.3);
      width: 100%;
      height: 100%;
      transition: background 0.3s ,left 0.3s cubic-bezier(0.78, 0.14, 0.15, 0.86) 0s, width 0s 0s , height 0s 0s;
    }
  }

  .sidebar-identify {
    padding: 16px 0;
    cursor: pointer;
    text-align: center;
    color: rgba(0, 0, 0, 0.48);

    &.dark {
      color: rgba(255, 255, 255, 0.48);
    }
  }
}

#xy-runtime-sidebar.xy-default-sidebar.ant-layout-sider {
  background-color: var(--xy-custom-sidebar-bg-color);
}

.xy-default-sidebar {
  padding: var(--xy-custom-sidebar-padding);
  &.collapsed{
    margin-right: 0;
  }

  .ant-layout-sider-children {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .ant-menu-inline{
    width: 100%;
  }

  // 隐藏antd默认的border
  .ant-menu-inline, .ant-menu-vertical, .ant-menu-vertical-left {
    border-right: 0;
  }

  #xy-sidebar-title {
    height: var(--xy-custom-sidebar-title-height);
    display: flex;
    align-items: center;
    gap: var(--xy-custom-sidebar-title-gap);
    padding: var(--xy-custom-sidebar-title-padding);
    margin: var(--xy-custom-sidebar-title-margin);
    border-bottom: var(--xy-custom-sidebar-title-border-bottom);
    box-sizing: border-box;
    transition: padding 0.3s;
    &.collapsed{
      padding: 0;
    }

    .xy-sidebar-logo {
      width: var(--xy-custom-sidebar-title-logo-width);
      height: var(--xy-custom-sidebar-title-logo-height);
      overflow-x: hidden;
    }
    .xy-sidebar-text {
      font-size: var(--xy-custom-sidebar-title-font-size);
      color: var(--xy-custom-sidebar-title-color);
      line-height: 30px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  #xy-sidebar-menu-title {
    margin: var(--xy-custom-sidebar-menu-title-margin);
    height: $menuItemHeight;
    display: flex;
    align-items: center;
    gap: var(--xy-custom-sidebar-menu-title-gap);
    padding: var(--xy-custom-sidebar-menu-title-padding);
    box-sizing: border-box;
    transition: padding 0.3s;
    &.collapsed{
      padding: 0;
    }

    .xy-sidebar-logo {
      width: var(--xy-custom-sidebar-menu-title-logo-size);
      height: var(--xy-custom-sidebar-menu-title-logo-size);
      background-color: var(--xy-custom-sidebar-menu-title-logo-bg-color);
      border-radius: 100%;
      display: flex;
      align-items: center;
      overflow-x: hidden;
      justify-content: center;
      .logo-icon{
        font-size: var( --xy-custom-sidebar-menu-title-logo-font-size);
        color: var(--xy-custom-sidebar-menu-title-logo-color);
        width: max-content;
        height: max-content;
      }
    }
    .xy-sidebar-text {
      font-family: PingFangSC-Medium;
      font-size: var(--xy-custom-sidebar-menu-title-text-font-size);
      color: var(--xy-custom-sidebar-menu-title-text-color);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  #xy-sidebar-menu-search {
    padding: var(--xy-custom-sidebar-menu-search-padding);
  }

  #xy-sidebar-menu {
    overflow-y: scroll !important;

    &::-webkit-scrollbar {
      width: var(--xy-custom-sidebar-scrollbar-size);
      height: var(--xy-custom-sidebar-scrollbar-size);
    }

    &::-webkit-scrollbar-track {
      background-color: transparent;
      border-radius: var(--xy-custom-sidebar-scrollbar-size);
    }

    &::-webkit-scrollbar-thumb {
      background-color: transparent;
      border-radius: var(--xy-custom-sidebar-scrollbar-size);
      border: 1px solid transparent;
      transition: background-color 0.2s;
    }

    &:focus-within,
    &:hover {
      &::-webkit-scrollbar-thumb {
        background-color: var(--xy-custom-sidebar-scrollbar-thumb-bg-color);

        &:hover {
          background-color: var(--xy-custom-sidebar-scrollbar-thumb-hover-bg-color);
        }
      }
    }

    &.ant-menu-root {
      flex: 1;
      width: 100%;
      overflow-y: auto;
      overflow-x: hidden;
    }
    &.hidden {
      display: none;
    }

    .first-menu-item.ant-menu-item,
    .first-menu-item.ant-menu-submenu > .ant-menu-submenu-title {
      padding-left: var(--xy-custom-sidebar-menu-item-left-padding-first) !important;
      font-size: var(--xy-custom-sidebar-font-size-first);
    }

    .second-menu-item.ant-menu-item,
    .second-menu-item.ant-menu-submenu > .ant-menu-submenu-title {
      padding-left: var(--xy-custom-sidebar-menu-item-left-padding-second) !important;
    }

    .third-menu-item.ant-menu-item,
    .third-menu-item.ant-menu-submenu > .ant-menu-submenu-title {
      padding-left: var(--xy-custom-sidebar-menu-item-left-padding-third) !important;
    }

    .forth-menu-item.ant-menu-item {
      padding-left: var(--xy-custom-sidebar-menu-item-left-padding-forth) !important;
    }

    .ant-menu-item, .ant-menu-submenu {
      padding-bottom: 0;
      .icon-placeholder {
        margin-left: -10px;
      }
      i.anticon {
        flex-shrink: 0;
      }
      .ellipsis {
        display: inline-block;
        text-overflow: ellipsis; // 在多行文本的情况下，用...隐藏超出范围的文本
        word-break: break-all;
        overflow: hidden;
      }
    }

    .ant-menu-submenu-open {
      .ant-menu {
        margin-bottom: var(--xy-custom-sidebar-sub-menu-bottom-margin);
      }
      .ant-menu-submenu-title,.ant-menu-item {
        margin: var(--xy-custom-sidebar-menu-open-item-margin);
      }
    }

    .ant-menu-item, .ant-menu-submenu-title {
      height: $menuItemHeight;
      line-height: $menuItemHeight;
      box-sizing: border-box;
      margin: var(--xy-custom-sidebar-menu-item-margin);
      padding-bottom: 0;
    }

    .ant-menu-item {
      padding-right: var(--xy-custom-sidebar-menu-item-right-padding);
    }
    .ant-menu-submenu-title {
      padding-right: var(--xy-custom-sidebar-menu-item-right-padding-arrow);
    }

    // 菜单收起的情况
    .ant-menu-inline-collapsed {
      .ant-menu-item, .ant-menu-submenu-title {
        display: flex;
        justify-content: center;
        .anticon {
          margin-right: 0px;
          line-height: 16px;
        }
      }
      .ant-menu-submenu-selected {
        .anticon {
          color: var(--wuji-primary-color);
        }
      }
    }

    .ant-menu-item, .ant-menu-submenu-title {
      width: var(--xy-custom-sidebar-menu-item-width);
      .anticon {
        margin-right: 8px;
        font-size: var(--xy-custom-sidebar-font-size-icon);
        width: var(--xy-custom-sidebar-font-size-icon);
        height: var(--xy-custom-sidebar-font-size-icon);
      }
      & > span {
        display: flex;
        align-items: center;
      }
    }

    .ant-menu-item {
      display: flex;
      align-items: center;
      font-size: var(--xy-custom-sidebar-font-size-second);
    }

    // 去除选中态右侧的边框
    .ant-menu-item::after {
      opacity: 0;
    }
  }
}


.header-hidden {
  display: none;
}

</style>
